<template>
  <teleport to="body">
    <transition name="fade-loader">
      <div v-if="isVisible" class="loader-overlay" :class="{ 'loader-overlay--hbc': customer === 'hammerbrooklyn' }"
        role="status" aria-live="polite" aria-atomic="true" :aria-label="label">
        <div class="loader-content">
          <div class="loader-emblem" aria-hidden="true">
            <span class="loader-orbit"></span>
            <div class="loader-badge">
              <img v-if="logo" :src="logo" alt="" class="loader-logo" />
              <span v-else class="loader-fallback"></span>
            </div>
          </div>
          <p class="loader-text">{{ label }}</p>
          <span class="loader-track" aria-hidden="true"><span></span></span>
        </div>
      </div>
    </transition>
  </teleport>
</template>

<script setup>
import { ref, watch, onMounted, onBeforeUnmount } from 'vue'
import { getLoaderLogoImage, getCustomer } from '@/assets/Branding/branding.js'

const props = defineProps({
  loading: { type: Boolean, required: true },
  label: { type: String, default: 'Loading…' },
  delay: { type: Number, default: 120 },
})
const isVisible = ref(false)
const logo = ref('')
const customer = ref('')
let timer
watch(() => props.loading, loading => {
  clearTimeout(timer)
  if (!loading) isVisible.value = false
  else timer = setTimeout(() => { isVisible.value = true }, Math.max(0, props.delay))
}, { immediate: true })
onMounted(async () => {
  try {
    [logo.value, customer.value] = await Promise.all([getLoaderLogoImage(), getCustomer()])
  } catch {
    // Progress remains visible if branding is unavailable.
  }
})
onBeforeUnmount(() => clearTimeout(timer))
</script>

<style scoped>
.loader-overlay {
  --loader-accent: #43a7f9;
  position: fixed;
  inset: calc(80px + env(safe-area-inset-top, 0px)) 0 calc(60px + env(safe-area-inset-bottom, 0px));
  z-index: 100;
  display: grid;
  place-items: center;
  padding: 16px;
  background: rgba(245, 248, 250, 0.48);
  backdrop-filter: blur(5px);
  -webkit-backdrop-filter: blur(5px);
  pointer-events: none;
}
.loader-overlay--hbc { --loader-accent: #c5fb13; background: rgba(17, 18, 20, 0.24); }
.loader-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 18px;
  width: min(210px, 100%);
  padding: 28px 24px 24px;
  border-radius: 24px;
  background: rgba(255, 255, 255, 0.96);
  border: 1px solid rgba(17, 24, 39, 0.08);
  box-shadow: 0 18px 60px rgba(17, 24, 39, 0.12);
  color: #263341;
}
.loader-overlay--hbc .loader-content { background: rgba(24, 26, 28, 0.96); border-color: rgba(255, 255, 255, 0.12); color: #f7f8f4; }
.loader-emblem { position: relative; display: grid; place-items: center; width: 72px; height: 72px; }
.loader-orbit { position: absolute; inset: 0; border-radius: 50%; border: 2px solid rgba(127, 139, 149, 0.16); border-top-color: var(--loader-accent); animation: loader-turn 1.2s linear infinite; }
.loader-badge { display: grid; place-items: center; width: 52px; height: 52px; border-radius: 16px; }
.loader-logo { width: 36px; height: 36px; object-fit: contain; }
.loader-fallback { width: 18px; height: 18px; border-radius: 6px; background: var(--loader-accent); }
.loader-text { margin: 0; font-size: 14px; font-weight: 600; line-height: 1.5; text-align: center; overflow-wrap: anywhere; }
.loader-track { width: 80px; height: 3px; overflow: hidden; border-radius: 4px; background: rgba(127, 139, 149, 0.18); }
.loader-track > span { display: block; width: 45%; height: 100%; border-radius: inherit; background: var(--loader-accent); animation: loader-progress 1.6s ease-in-out infinite; }
.fade-loader-enter-active, .fade-loader-leave-active { transition: opacity 160ms ease; }
.fade-loader-enter-from, .fade-loader-leave-to { opacity: 0; }
@keyframes loader-turn { to { transform: rotate(360deg); } }
@keyframes loader-progress { from { transform: translateX(-110%); } to { transform: translateX(330%); } }
@media (max-height: 420px) {
  .loader-content { padding: 12px 20px; gap: 8px; }
  .loader-emblem { width: 48px; height: 48px; }
  .loader-badge { width: 36px; height: 36px; }
  .loader-logo { width: 26px; height: 26px; }
}
@media (prefers-reduced-motion: reduce) {
  .loader-orbit, .loader-track > span { animation: none; }
  .fade-loader-enter-active, .fade-loader-leave-active { transition: none; }
}
</style>
